import { Box, Card, IconButton } from '@mui/material'

import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CustomButton from '../../../../Components/CustomButton/CustomButton'
import WTypography from '../../../../Components/whiteTypography'
import { AppContext } from '../../../../context'
import CFBarChart from '../Charts/BarChart'
import DonutChart1 from '../Charts/IncomeDonut'
import BarCIcone from '../Icone/bar_chart_white_24dp.svg'
import DonutCIcone from '../Icone/donut_small_white_24dp.svg'
import { makeGetRequest } from 'utils/api'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { deviseCal } from 'utils/helpers'
import FinanceEmptyShortcut from './FinanceEmptyShortcut'

const hasNonZeroValue = (value) => Number(value || 0) !== 0

const hasDashboardData = (report) => {
  if (!report?.calculation) return false

  if (report.nom === 'actifs') {
    return report.calculation?.chart_type?.actifs?.data?.some(hasNonZeroValue)
  }

  if (report.nom === 'passifs') {
    return report.calculation?.chart_type?.passifs?.data?.some(hasNonZeroValue)
  }

  if (report.calcul === 'cashflow.js') {
    return (
      hasNonZeroValue(report.calculation.header_value) ||
      hasNonZeroValue(report.calculation.totalRev) ||
      hasNonZeroValue(report.calculation.totalDep)
    )
  }

  return (
    hasNonZeroValue(report.calculation.header_value) ||
    report.calculation.group_of_categories?.some((cat) => hasNonZeroValue(cat.aggregate))
  )
}

function ChartCart1({ oldData, currency }) {
  // const theme = useTheme()
  const siteDevise = useSelector((store) => store.devise)
  const getAppStore = useSelector((store) => store.appStore).appStore
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  const [optionChart, setOptionChart] = useState(false)
  const [reports, setReports] = useState()
  const [headerValue, setHeaderValue] = useState(null)
  const { tableAccount } = useContext(AppContext)

  const navigate = useNavigate()
  const activeReport = reports || oldData
  const hasData = hasDashboardData(activeReport)
  const yearData = async (yearOrDate) => {
    const currencyParam = currency ? `&currency=${currency}` : ''
    const { data } = await makeGetRequest(
      `/api/Finance/reports/${yearOrDate}/?calcul=${oldData.calcul}&account=${tableAccount}${currencyParam}`,
      {},
      true,
    )
    setReports({ ...data })
  }

  useEffect(() => {
    // const getReports = async () => {
    //   if ((oldData.nom === 'revenus' || oldData.nom === 'depenses') && tableAccount !== '') {
    //     const { data } = await makeGetRequest(
    //       `/api/Finance/revenus-despense/${tableAccount}/?type_operation=${oldData.nom}`,
    //       {},
    //       true,
    //     )
    //     setReports({ ...data })
    //   }
    // }
    if (tableAccount !== '') yearData('month')
  }, [tableAccount])

  // LAP-733 (GE022) — route by stable `calcul` discriminator, not by localized titre.
  // Pre-i18n this branched on French strings (e.g. titre === 'Mes revenus'), which
  // failed as soon as the backend started serving translated titres.
  const handleDashboardDetail = (e) => {
    const calcul = (reports && reports.calcul) || (oldData && oldData.calcul)
    switch (calcul) {
      case 'revenus.js':
        return navigate('/dashboard-detail/R')
      case 'depenses.js':
        return navigate('/dashboard-detail/D')
      case 'cashflow.js':
        return navigate('/dashboard-detail/moncashflow')
      case 'liabilities.js':
        return navigate('/dashboard-detail/mespassifs')
      case 'assets.js':
        return navigate('/dashboard-detail/mesactifs')
      default:
        return
    }
  }
  // LAP-733 (GE022) — title is now derived from the stable `calcul` discriminator
  // and resolved against the frontend i18n bundle. This stays correct even if the
  // backend ReportCart i18n migration hasn't been deployed yet (the previous code
  // surfaced the raw FR `titre` column in that case).
  const titleByCalcul = {
    'revenus.js':     'finance.fiStatement.myIncome',
    'depenses.js':    'finance.fiStatement.myExpenses',
    'cashflow.js':    'finance.fiStatement.myCashflow',
    'assets.js':      'finance.fiStatement.myAssets',
    'liabilities.js': 'finance.fiStatement.myLiabilities',
  }
  const resolveTitle = (src) => {
    if (!src) return ''
    const key = titleByCalcul[src.calcul]
    if (key) {
      const translated = t(key)
      // i18next returns the key itself when missing — fall through to backend titre.
      if (translated && translated !== key) return translated
    }
    return src.titre || ''
  }
  return (
    <Card className="dashboard_card__root ">
      <Box onClick={handleDashboardDetail} className="dashboard_card__header no-drag">
        <WTypography component={'span'} variant="h5">
          {resolveTitle(reports || oldData)}
        </WTypography>
        <WTypography component={'span'} variant="h5">
          {headerValue
            ? headerValue
            : reports
            ? deviseCal(reports.calculation.header_value, siteDevise.devise.to_eur)
            : oldData
            ? deviseCal(oldData.calculation.header_value, siteDevise.devise.to_eur)
            : ''}

          {/* {oldData && oldData.calculation.header_value} */}
        </WTypography>
      </Box>

      <WTypography variant={'body2'}>
        {/* LAP-733 (GE022) — branch on stable `calcul` rather than localized titre,
            covering all 5 card variants. Falls back to backend nom_graph when calcul
            is unknown. */}
        {(() => {
          const active = reports || oldData
          if (!active) return ''
          const subtitleByCalcul = {
            'revenus.js':     'finance.fiStatement.distributionRevenus',
            'depenses.js':    'finance.fiStatement.distributionDepenses',
            'cashflow.js':    'finance.fiStatement.distributionCashflow',
            'assets.js':      'finance.fiStatement.distributionAssets',
            'liabilities.js': 'finance.fiStatement.distributionLiabilities',
          }
          const key = subtitleByCalcul[active.calcul]
          if (key) {
            const translated = t(key)
            if (translated && translated !== key) return translated
          }
          return active.nom_graph
        })()}
      </WTypography>
      <Box mt={3} minWidth={3} mb={6} height={'100%'}>
        {!hasData ? (
          <FinanceEmptyShortcut />
        ) : oldData && oldData.calcul === 'cashflow.js' ? (
          <CFBarChart reports={reports ? reports : oldData} />
        ) : optionChart ? (
          <CFBarChart revenusDespense={true} reports={reports ? reports : oldData} />
        ) : (
          <DonutChart1
            setHeaderValue={setHeaderValue}
            reports={reports ? reports : oldData}
            yearData={yearData}
            oldData={oldData}
          />
        )}
      </Box>

      {oldData &&
        (oldData.calcul === 'revenus.js' ||
          oldData.calcul === 'depenses.js' ||
          oldData.calcul === 'cashflow.js') && (
          <Box mt={2} className="dashboard_card__filters no-drag">
            <Box>
              <CustomButton onClick={(e) => yearData('year')} style={{ padding: 0 }}>
                {t('finance.fiStatement.year')}
              </CustomButton>
              <CustomButton onClick={(e) => yearData('month')} style={{ padding: 0 }}>
                {t('finance.fiStatement.month')}
              </CustomButton>
            </Box>
            {oldData.calcul !== 'cashflow.js' && (
              <Box>
                <IconButton onClick={() => setOptionChart(true)}>
                  <img src={BarCIcone} alt="BarChart" className="Optionbutton"></img>
                </IconButton>
                <IconButton onClick={() => setOptionChart(false)}>
                  <img src={DonutCIcone} alt="DonutChart" className="Optionbutton"></img>
                </IconButton>
              </Box>
            )}
          </Box>
        )}
    </Card>
  )
}

export default ChartCart1
